import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { getDefaultConditions } from "@redotech/redo-conversion-core-common/post-purchase/node-defaults";
import {
  customerTagsSegmentSchema,
  orderValueSegmentSchema,
  previousOrderCountSegmentSchema,
  productCollectionSegmentSchema,
  productMetafieldsSegmentSchema,
  productOrderedSegmentSchema,
  productTagsSegmentSchema,
  SegmentablePostPurchaseCharacteristic,
} from "@redotech/redo-conversion-core-common/post-purchase/post-purchase-flow";
import {
  PostPurchaseConditionBlockInProgress,
  PostPurchaseConditionInProgress,
} from "@redotech/redo-conversion-core-common/post-purchase/segment-in-progress";
import PlusIcon from "@redotech/redo-icons/plus.svg";
import TrashIcon from "@redotech/redo-icons/trash-01.svg";
import { ConditionConnector } from "@redotech/redo-web-marketing/marketing/segments/condition-connector";
import { SegmentConjunctionSelect } from "@redotech/redo-web-marketing/marketing/segments/segment-conjunction-select";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { TextRedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoSingleSelectDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { TeamContext } from "@redotech/redo-web/context/team-context";
import { Flex, FlexProps } from "@redotech/redo-web/flex";
import { ModalProduct } from "@redotech/redo-web/product/product-selection-modal";
import { CollectionInfo } from "@redotech/shopify-storefront";
import { ProductInfo } from "@redotech/shopify-storefront/products";
import { Gid } from "@redotech/shopify-util/id";
import { interleave, remove, replace } from "@redotech/util/array";
import { assertNever } from "@redotech/util/type";
import { memo, useMemo } from "react";
import {
  CustomerTagsSegmentCondition,
  CustomerTagsSegmentProps,
} from "./segment-condition/customer-tags";
import {
  OrderValueSegmentCondition,
  OrderValueSegmentProps,
} from "./segment-condition/order-value";
import {
  PreviousOrderCountSegmentCondition,
  PreviousOrderCountSegmentProps,
} from "./segment-condition/previous-order-count";
import {
  ProductCollectionsSegmentCondition,
  ProductCollectionsSegmentProps,
} from "./segment-condition/product-collections";
import {
  ProductMetafieldsSegmentCondition,
  ProductMetafieldsSegmentProps,
} from "./segment-condition/product-metafields";
import {
  ProductTagsSegmentCondition,
  ProductTagsSegmentProps,
} from "./segment-condition/product-tags";
import {
  ProductsOrderedSegmentCondition,
  ProductsOrderedSegmentProps,
} from "./segment-condition/products-ordered";

export const blockBorderProps: FlexProps = {
  borderColor: "primary",
  borderStyle: "solid",
  borderWidth: "1px",
};

const postPurchaseCharacteristicLabel: Record<
  SegmentablePostPurchaseCharacteristic,
  string
> = {
  [SegmentablePostPurchaseCharacteristic.ORDER_VALUE]: "Order value",
  [SegmentablePostPurchaseCharacteristic.PRODUCTS_ORDERED]: "Products ordered",
  [SegmentablePostPurchaseCharacteristic.PRODUCT_COLLECTIONS]:
    "Product collections",
  [SegmentablePostPurchaseCharacteristic.PRODUCT_METAFIELDS]:
    "Product metafields",
  [SegmentablePostPurchaseCharacteristic.PRODUCT_TAGS]: "Product tags",
  [SegmentablePostPurchaseCharacteristic.CUSTOMER_TAGS]: "Customer tags",
  [SegmentablePostPurchaseCharacteristic.PREVIOUS_ORDER_COUNT]:
    "Previous order count",
};

interface CommonProps {
  block: PostPurchaseConditionBlockInProgress;
  getProductsByGids: (ids: Gid[]) => Promise<ProductInfo[]>;
  getCollectionsByGids: (ids: Gid[]) => Promise<CollectionInfo[]>;
}

export type PostPurchaseSegmentConditionBlockProps = CommonProps &
  (
    | { readonly: true }
    | {
        readonly?: false | undefined;
        searchProducts: (params: { search: string }) => Promise<ModalProduct[]>;
        searchCollections: (params: {
          search: string;
        }) => Promise<CollectionInfo[]>;
        searchProductTags: (params: { search: string }) => Promise<string[]>;
        searchCustomerTags: (params: {
          search: string;
        }) => Promise<{ tags: string[]; total: number }>;
        setBlock: (block: PostPurchaseConditionBlockInProgress) => void;
        deleteBlockRequested: () => void;
      }
  );

export const PostPurchaseSegmentConditionBlock = memo(
  function PostPurchaseSegmentConditionBlock({
    block,
    getProductsByGids,
    getCollectionsByGids,
    ...props
  }: PostPurchaseSegmentConditionBlockProps) {
    const conditionElements = block.conditions.map((condition, index) => {
      if (!condition) {
        return null;
      }

      const displayProps: PostPurchaseConditionDisplayProps = (() => {
        if (props.readonly) {
          return {
            condition,
            getProductsByGids,
            getCollectionsByGids,
            readonly: true,
          };
        }

        return {
          condition,
          getProductsByGids,
          getCollectionsByGids,
          searchProducts: props.searchProducts,
          searchCollections: props.searchCollections,
          searchProductTags: props.searchProductTags,
          searchCustomerTags: props.searchCustomerTags,
          readonly: false,
          setCondition: (updatedCondition) => {
            props.setBlock({
              ...block,
              conditions: replace(block.conditions, index, updatedCondition),
            });
          },
          deleteRequested: () => {
            props.setBlock({
              ...block,
              conditions: remove(block.conditions, index),
            });
          },
        };
      })();

      return <PostPurchaseConditionDisplay {...displayProps} key={index} />;
    });

    const joinedConditions = interleave(conditionElements, (_, idx) => (
      <ConditionConnector
        conjunction={block.conjunction}
        key={`connector-${idx}`}
      />
    ));

    return (
      <Flex
        {...blockBorderProps}
        bgColor="primary"
        borderRadius="lg"
        dir="column"
        gap="xl"
        p="xl"
      >
        <Flex justify="space-between">
          <SegmentConjunctionSelect
            mode={block.conjunction}
            textAfterConjunction="of these conditions must match the order"
            {...(props.readonly
              ? { readonly: true }
              : {
                  readonly: false,
                  setMode: (mode) =>
                    props.setBlock({ ...block, conjunction: mode }),
                })}
          />
          {!props.readonly && (
            <RedoButton
              aria-label="Delete"
              IconLeading={TrashIcon}
              onClick={props.deleteBlockRequested}
              theme="destructive"
            />
          )}
        </Flex>
        <Flex dir="column" gap="none">
          {joinedConditions}
        </Flex>
        {!props.readonly && (
          <Flex>
            <RedoButton
              hierarchy="secondary"
              IconLeading={PlusIcon}
              onClick={() => {
                props.setBlock({
                  ...block,
                  conditions: [
                    ...block.conditions,
                    { segmentableCharacteristic: undefined },
                  ],
                });
              }}
              text="Add condition"
            />
          </Flex>
        )}
      </Flex>
    );
  },
);

interface PostPurchaseConditionDisplayCommonProps {
  condition: PostPurchaseConditionInProgress;
  getProductsByGids: (ids: Gid[]) => Promise<ProductInfo[]>;
  getCollectionsByGids: (ids: Gid[]) => Promise<CollectionInfo[]>;
}

type PostPurchaseConditionDisplayProps =
  PostPurchaseConditionDisplayCommonProps &
    (
      | { readonly: true }
      | {
          readonly?: false | undefined;
          searchProducts: (params: {
            search: string;
          }) => Promise<ModalProduct[]>;
          searchCollections: (params: {
            search: string;
          }) => Promise<CollectionInfo[]>;
          searchProductTags: (params: { search: string }) => Promise<string[]>;
          searchCustomerTags: (params: {
            search: string;
          }) => Promise<{ tags: string[]; total: number }>;
          setCondition: (condition: PostPurchaseConditionInProgress) => void;
          deleteRequested: () => void;
        }
    );

const PostPurchaseConditionDisplay = memo(
  function PostPurchaseConditionDisplay({
    condition,
    getProductsByGids,
    getCollectionsByGids,
    ...props
  }: PostPurchaseConditionDisplayProps) {
    const team = useRequiredContext(TeamContext);

    const conditionContent = (() => {
      if (!condition.segmentableCharacteristic) {
        return null;
      }

      switch (condition.segmentableCharacteristic) {
        case SegmentablePostPurchaseCharacteristic.ORDER_VALUE: {
          const parsedCondition = orderValueSegmentSchema.parse(condition);
          const orderValueSegmentProps: OrderValueSegmentProps = props.readonly
            ? { condition: parsedCondition, readonly: true }
            : {
                condition: parsedCondition,
                readonly: false,
                setCondition: props.setCondition,
              };

          return <OrderValueSegmentCondition {...orderValueSegmentProps} />;
        }
        case SegmentablePostPurchaseCharacteristic.PRODUCTS_ORDERED: {
          const parsedCondition = productOrderedSegmentSchema.parse(condition);
          const productsOrderedSegmentProps: ProductsOrderedSegmentProps =
            props.readonly
              ? {
                  condition: parsedCondition,
                  getProductsByGids,
                  readonly: true,
                }
              : {
                  condition: parsedCondition,
                  getProductsByGids,
                  searchProducts: props.searchProducts,
                  readonly: false,
                  setCondition: props.setCondition,
                };

          return (
            <ProductsOrderedSegmentCondition {...productsOrderedSegmentProps} />
          );
        }
        case SegmentablePostPurchaseCharacteristic.PRODUCT_COLLECTIONS: {
          const parsedCondition =
            productCollectionSegmentSchema.parse(condition);
          const productCollectionsSegmentProps: ProductCollectionsSegmentProps =
            props.readonly
              ? {
                  condition: parsedCondition,
                  getCollectionsByGids,
                  readonly: true,
                }
              : {
                  condition: parsedCondition,
                  getCollectionsByGids,
                  searchCollections: props.searchCollections,
                  readonly: false,
                  setCondition: props.setCondition,
                };

          return (
            <ProductCollectionsSegmentCondition
              {...productCollectionsSegmentProps}
            />
          );
        }
        case SegmentablePostPurchaseCharacteristic.PRODUCT_METAFIELDS: {
          const parsedCondition =
            productMetafieldsSegmentSchema.parse(condition);
          const productMetafieldsSegmentProps: ProductMetafieldsSegmentProps =
            props.readonly
              ? { condition: parsedCondition, readonly: true }
              : {
                  condition: parsedCondition,
                  readonly: false,
                  setCondition: props.setCondition,
                };

          return (
            <ProductMetafieldsSegmentCondition
              {...productMetafieldsSegmentProps}
            />
          );
        }
        case SegmentablePostPurchaseCharacteristic.PRODUCT_TAGS: {
          const parsedCondition = productTagsSegmentSchema.parse(condition);
          const productTagsSegmentProps: ProductTagsSegmentProps =
            props.readonly
              ? { condition: parsedCondition, readonly: true }
              : {
                  condition: parsedCondition,
                  readonly: false,
                  setCondition: props.setCondition,
                  searchProductTags: props.searchProductTags,
                };

          return <ProductTagsSegmentCondition {...productTagsSegmentProps} />;
        }
        case SegmentablePostPurchaseCharacteristic.CUSTOMER_TAGS: {
          const parsedCondition = customerTagsSegmentSchema.parse(condition);
          const customerTagsSegmentProps: CustomerTagsSegmentProps =
            props.readonly
              ? { condition: parsedCondition, readonly: true }
              : {
                  condition: parsedCondition,
                  readonly: false,
                  setCondition: props.setCondition,
                  searchCustomerTags: props.searchCustomerTags,
                };

          return <CustomerTagsSegmentCondition {...customerTagsSegmentProps} />;
        }
        case SegmentablePostPurchaseCharacteristic.PREVIOUS_ORDER_COUNT: {
          const parsedCondition =
            previousOrderCountSegmentSchema.parse(condition);
          const previousOrderCountSegmentProps: PreviousOrderCountSegmentProps =
            props.readonly
              ? { condition: parsedCondition, readonly: true }
              : {
                  condition: parsedCondition,
                  readonly: false,
                  setCondition: props.setCondition,
                };

          return (
            <PreviousOrderCountSegmentCondition
              {...previousOrderCountSegmentProps}
            />
          );
        }
        default:
          assertNever(condition);
      }
    })();

    const handleCharacteristicSelected = useHandler(
      (characteristic: SegmentablePostPurchaseCharacteristic) => {
        if (props.readonly) {
          return;
        }

        const defaultCondition = getDefaultConditions(team)[characteristic];
        props.setCondition(defaultCondition);
      },
    );

    return (
      <Flex
        {...blockBorderProps}
        align="flex-start"
        bgColor={props.readonly ? "secondary" : "primary"}
        borderRadius="md"
        gap="md"
        p="md"
      >
        <Flex dir="column" flex={1} gap="xl" minW="none">
          {!props.readonly && (
            <PostPurchaseSegmentCharacteristicSelector
              onCharacteristicSelected={handleCharacteristicSelected}
              selectedCharacteristic={condition.segmentableCharacteristic}
            />
          )}
          <Flex align="flex-start" dir="column" gap="md">
            {conditionContent}
          </Flex>
        </Flex>
        {!props.readonly && (
          <RedoButton
            aria-label="Delete"
            IconLeading={TrashIcon}
            onClick={props.deleteRequested}
            size="xs"
            theme="destructive"
          />
        )}
      </Flex>
    );
  },
);

const PostPurchaseSegmentCharacteristicSelector = memo(
  function PostPurchaseSegmentCharacteristicSelector({
    onCharacteristicSelected,
    selectedCharacteristic,
  }: {
    selectedCharacteristic: SegmentablePostPurchaseCharacteristic | undefined;
    onCharacteristicSelected: (
      characteristic: SegmentablePostPurchaseCharacteristic,
    ) => void;
  }) {
    const characteristicOptions: TextRedoListItem<SegmentablePostPurchaseCharacteristic>[] =
      useMemo(() => {
        return Object.values(SegmentablePostPurchaseCharacteristic).map(
          (characteristic) => ({
            type: "text",
            id: characteristic,
            value: characteristic,
            text: postPurchaseCharacteristicLabel[characteristic],
          }),
        );
      }, []);

    const selectedItem = useMemo(() => {
      return characteristicOptions.find(
        (option) => option.value === selectedCharacteristic,
      );
    }, [characteristicOptions, selectedCharacteristic]);

    return (
      <RedoSingleSelectDropdownInput<SegmentablePostPurchaseCharacteristic>
        fitToAnchor={false}
        label="Condition type"
        options={characteristicOptions}
        optionSelected={({ item }) => {
          onCharacteristicSelected(item.value);
        }}
        placeholder="Select a condition"
        selectedItem={selectedItem}
      />
    );
  },
);
